import { AddFolderIcon, BackIcon, FolderIcon, LoadingIcon, VolumeIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { useGraphqlPagination } from '@components/infiniteScroll';
import { Typography } from '@components/typography';
import {
  useGetVolumeDataQuery,
  useGetVolumesNamesListQuery,
  type GetVolumeDataQuery,
  type GetVolumesNamesListQuery,
  type VolumeData,
} from '@generated/graphql';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { Box, Divider, Popover, Stack, StackProps, useTheme } from '@mui/material';
import { GraphDataContext, ZoomContext } from '@subsets/workspaces';
import { ChannelNode } from '@subsets/workspaces/graphs/graph/types';
import { Dispatch, SetStateAction, useContext, useMemo, useState } from 'react';

const VolumeNodeTooltip = ({ name, thumbnailUrl }: { name: string; thumbnailUrl: string }) => {
  const { palette } = useTheme();
  return (
    <Stack
      gap={1}
      width="256px"
      sx={{
        border: `8px solid ${palette.grey[900]}`,
        backgroundColor: palette.grey[900],
      }}
    >
      {thumbnailUrl && <img src={thumbnailUrl} alt={name} />}
    </Stack>
  );
};

type VolumeViewRowProps = {
  name: string;
  volumeName: string;
  type: string;
  volumeId: string;
  folderHistory: string[];
  hash?: string;
  thumbnailUrl?: string;
  setFolderHistory: Dispatch<SetStateAction<string[]>>;
};

type VolumeRowProps = {
  name: string;
  onBack: () => void;
  volumeId: string;
};

const VolumeViewRow = ({
  name,
  volumeName,
  type,
  volumeId,
  thumbnailUrl,
  folderHistory,
  hash,
  setFolderHistory,
}: VolumeViewRowProps) => {
  const { palette } = useTheme();
  const { createNode } = useContext(GraphDataContext);
  const { getCenterPosition } = useContext(ZoomContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const onClick = async (key: string) => {
    if (type === 'folder') {
      setFolderHistory((prevState) => [...prevState, key]);
    } else if (type === 'file') {
      const folder =
        folderHistory.length > 0 ? ['', ...folderHistory].join('/') : folderHistory.join('/');
      const newNodeData: ChannelNode = {
        color: '#246BB3',
        alias: key.split('.')[0],
        name: 'VolumeFile',
        category: 'Object',
        subcategory: 'Generators',
        hash: hash || 'a7c19eb160150ee04d82af60c9332d104f0a7f89',
        tooltip: `${volumeName}:${folder}/${key}`,
        inputs: [
          {
            name: 'File',
            default: `${volumeId}:${folder}/${key}`,
            description: '',
            hidden: true,
          },
        ],
        outputs: [{ name: 'File', description: '' }],
      };
      await createNode(newNodeData, getCenterPosition());
    } else if (type === 'directory') {
      const folder =
        folderHistory.length > 0 ? ['', ...folderHistory].join('/') : folderHistory.join('/');
      const directoryNodeName = `${volumeName}:${folder}/`;
      const newNodeData: ChannelNode = {
        color: '#246BB3',
        name: 'VolumeDirectory',
        alias: `${folder || volumeName}/`,
        category: 'Object',
        subcategory: 'Generators',
        hash: 'a7c19eb160150ee04d82af60c9332d104f0a7f89',
        tooltip: directoryNodeName,
        inputs: [
          {
            name: 'Directory',
            default: `${volumeId}:${folder}/`,
            description: '',
            hidden: true,
          },
        ],
        outputs: [{ name: 'Directory', description: '' }],
      };
      await createNode(newNodeData, getCenterPosition());
    }
  };

  return (
    <>
      <Stack
        direction="row"
        gap={4}
        alignItems="center"
        px={2}
        py={1.5}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            background: palette.table.row.hover,
          },
        }}
        onClick={() => onClick(name)}
        onMouseEnter={(event) => (type === 'file' ? setAnchorEl(event.currentTarget) : {})}
        onMouseLeave={() => (type === 'file' ? setAnchorEl(null) : {})}
      >
        <Stack width="24px" alignItems="center" justifyContent="center">
          {type === 'folder' && <FolderIcon size={20} />}
          {type === 'directory' && <AddFolderIcon size={20} />}
        </Stack>
        <Typography variant="body2" handleOverFlow>
          {name}
        </Typography>
        {Boolean(anchorEl) && Boolean(thumbnailUrl) && (
          <Box
            sx={{
              width: 0,
              height: 0,
              borderTop: '12px solid transparent',
              borderBottom: '12px solid transparent',
              borderRight: `12px solid ${palette.grey[900]}`,
              position: 'absolute',
              right: '0px',
            }}
          />
        )}
      </Stack>
      <Popover
        open={Boolean(anchorEl) && Boolean(thumbnailUrl)}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        disablePortal
        style={{ pointerEvents: 'none' }}
        hideBackdrop
        sx={{
          '& .MuiPopover-paper': {
            backgroundColor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
          },
        }}
      >
        <VolumeNodeTooltip thumbnailUrl={thumbnailUrl} name={name} />
      </Popover>
    </>
  );
};

const VolumeView = ({ name, onBack, volumeId }: VolumeRowProps) => {
  const [folderHistory, setFolderHistory] = useState<string[]>([]);

  const { data, loading, fetchMore, hasMore } = useGraphqlPagination<
    GetVolumeDataQuery,
    VolumeData
  >(
    useGetVolumeDataQuery({
      variables: {
        volumeId,
        dir: folderHistory.length > 0 ? folderHistory.join('/') : '/',
        limit: 50,
      },
    }),
    'getVolumeData',
    'key',
  );

  const volumeData = useMemo(
    () =>
      data?.map((item) => ({
        ...item,
        type: item?.hash?.length > 0 ? 'file' : 'folder',
        name: item?.key?.split('/')[0],
      })) || [],
    [data],
  );

  const sentinelRef = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: fetchMore,
  });

  const handleBackArrowClick = () => {
    if (folderHistory.length === 0) {
      onBack();
    } else {
      setFolderHistory((prevState) => prevState.slice(0, -1));
    }
  };

  return (
    <Stack gap={4} pt={3} height="100%">
      <Stack direction="row" alignItems="center" gap={2}>
        <IconButton onClick={handleBackArrowClick} Icon={BackIcon} size={20} />
        <VolumeIcon />
        <Typography variant="body2" handleOverFlow>
          {`${name} : ${folderHistory.join('/')}`}
        </Typography>
      </Stack>

      {!loading && volumeData.length !== 0 && (
        <Stack gap={2} pl={8} flex={1}>
          <VolumeViewRow
            key="directory_node"
            volumeId={volumeId}
            folderHistory={folderHistory}
            setFolderHistory={setFolderHistory}
            volumeName={name}
            name="Add entire directory"
            type="directory"
          />
          <Divider />
          <Stack>
            {volumeData.map((item) => (
              <VolumeViewRow
                key={item.key}
                volumeId={volumeId}
                folderHistory={folderHistory}
                setFolderHistory={setFolderHistory}
                {...item}
                volumeName={name}
              />
            ))}
          </Stack>
          <Box ref={sentinelRef} />
        </Stack>
      )}

      {loading && (
        <Stack direction="row" justifyContent="center">
          <LoadingIcon size={14} />
        </Stack>
      )}

      {volumeData.length === 0 && !loading && (
        <Stack direction="row" justifyContent="center">
          <Typography variant="body2">No volume data available</Typography>
        </Stack>
      )}
    </Stack>
  );
};

const ChannelVolumeTreeItem = ({ name, ...props }: { name: string } & StackProps) => {
  const { palette } = useTheme();
  return (
    <Stack
      py={3}
      px={2}
      direction="row"
      alignItems="center"
      gap={2}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          background: palette.table.row.hover,
        },
      }}
      {...props}
    >
      <VolumeIcon />
      <Typography variant="body2" handleOverFlow disableTooltip>
        {name}
      </Typography>
    </Stack>
  );
};

type VolumesListItem = GetVolumesNamesListQuery['getVolumes'] extends readonly (infer ElementType)[]
  ? ElementType
  : never;

export const ChannelVolumeTreeView = ({ workspaceId }: { workspaceId: string }) => {
  const [selectedVolume, setSelectedVolume] = useState<VolumesListItem>();
  const { data, loading, fetchMore, hasMore } = useGraphqlPagination<
    GetVolumesNamesListQuery,
    VolumesListItem
  >(
    useGetVolumesNamesListQuery({ variables: { workspaceId, limit: 50 } }),
    'getVolumes',
    'volumeId',
  );
  const sentinelRef = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: fetchMore,
  });

  if (loading) return <Box />;

  return (
    <Box height="100%">
      {!selectedVolume && (
        <Box>
          {data?.map((volumeData) => (
            <ChannelVolumeTreeItem
              key={volumeData?.volumeId}
              name={volumeData?.name}
              onClick={() => setSelectedVolume(volumeData)}
            />
          ))}
          {hasMore && (
            <Box
              ref={sentinelRef}
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
              py={4}
            >
              <LoadingIcon size={14} />
            </Box>
          )}
        </Box>
      )}

      {selectedVolume && (
        <VolumeView
          name={selectedVolume.name}
          volumeId={selectedVolume.volumeId}
          onBack={() => setSelectedVolume(undefined)}
        />
      )}
    </Box>
  );
};
